import { useMemo, useState } from "react";
import { Plus, Check, RotateCcw, Trash2, AlertCircle } from "lucide-react";
import { Page, Panel, SearchInput, usePagination, Pagination } from "../lib/ui";
import { api, humanDate } from "../lib/api";

const PRIORITY_LABELS = { low: "Düşük", normal: "Normal", high: "Yüksek" };
const PRIORITY_CLASS = { low: "muted", normal: "info", high: "danger" };

export default function Notes({ rows, setModal, onReload, user }) {
  const [tab, setTab] = useState("open");
  const [q, setQ] = useState("");
  const isAdmin = user?.role === "admin";

  const filtered = useMemo(() => rows.filter((n) => {
    if (tab === "open" && n.status !== "open") return false;
    if (tab === "done" && n.status !== "done") return false;
    if (q) {
      const bag = [n.title, n.body, n.created_by_name].filter(Boolean).map((x) => x.toLowerCase()).join(" ");
      return bag.includes(q.toLowerCase());
    }
    return true;
  }), [rows, tab, q]);

  const counts = useMemo(() => ({
    all: rows.length,
    open: rows.filter((n) => n.status === "open").length,
    done: rows.filter((n) => n.status === "done").length,
  }), [rows]);
  const pg = usePagination(filtered);

  const markDone = async (n) => {
    await api.put(`/notes/${n.id}/done`);
    onReload();
  };
  const reopen = async (n) => {
    await api.put(`/notes/${n.id}/reopen`);
    onReload();
  };
  const remove = async (n) => {
    if (!window.confirm(`"${n.title}" notunu silmek istediğinize emin misiniz?`)) return;
    await api.delete(`/notes/${n.id}`);
    onReload();
  };

  return (
    <>
      <Page
        eyebrow="EKİP · YAPILACAKLAR"
        title="Notlar & Yapılacak İşler"
        subtitle={isAdmin
          ? "Ekibinize iş atayın, tamamlanınca işaretlendiğinde bildirim düşer."
          : "Size atanan işleri işaretleyin. Yönetici kontrol edip listeden düşecektir."}
        action={
          <button data-testid="add-notes-button" className="primary-button" disabled={!isAdmin}
                  title={!isAdmin ? "Sadece yönetici not ekleyebilir" : ""}
                  onClick={() => setModal({ type: "note" })}>
            <Plus size={17} /> Yeni not
          </button>
        }
      />
      <Panel className="module-panel"
             action={
               <div className="filter-row">
                 <div className="tab-group">
                   {[["open", "Yapılacak"], ["done", "Tamam"], ["all", "Tümü"]].map(([id, label]) => (
                     <button key={id} data-testid={`notes-tab-${id}`}
                             type="button" className={tab === id ? "tab active" : "tab"}
                             onClick={() => setTab(id)}>
                       {label} <b>{counts[id]}</b>
                     </button>
                   ))}
                 </div>
                 <SearchInput value={q} onChange={setQ} testid="notes-search" placeholder="Başlık, içerik" />
               </div>
             }
      >
        {filtered.length === 0 ? (
          <div className="line-empty" style={{ padding: 40 }}>Bu sekmede not yok.</div>
        ) : (
          <div className="notes-list" data-testid="notes-list">
            {pg.slice.map((n) => {
              const overdue = n.status === "open" && n.due_date && n.due_date < new Date().toISOString().slice(0, 10);
              return (
                <div key={n.id} className={`note-card ${n.status === "done" ? "note-done" : ""} ${overdue ? "note-overdue" : ""}`}
                     data-testid={`note-card-${n.id}`}>
                  <div className="note-head">
                    <span className={`priority-badge tone-${PRIORITY_CLASS[n.priority] || "info"}`}>
                      {PRIORITY_LABELS[n.priority] || "Normal"}
                    </span>
                    <strong>{n.title}</strong>
                    {overdue && (
                      <span className="warn-tag danger" data-testid={`note-overdue-${n.id}`}>
                        <AlertCircle size={11} /> Gecikti
                      </span>
                    )}
                  </div>
                  {n.body && <p className="note-body">{n.body}</p>}
                  <div className="note-meta">
                    <span>Ekleyen: <strong>{n.created_by_name || "-"}</strong></span>
                    <span>Oluşturma: {humanDate(n.created_at)}</span>
                    {n.due_date && <span>Bitiş: <strong>{n.due_date}</strong></span>}
                    {n.status === "done" && (
                      <span data-testid={`note-doneby-${n.id}`}>
                        ✓ {n.done_by_name} · {humanDate(n.done_at)}
                      </span>
                    )}
                  </div>
                  <div className="note-actions">
                    {n.status === "open" ? (
                      <button data-testid={`note-done-${n.id}`} className="secondary-button" onClick={() => markDone(n)}>
                        <Check size={14} /> Tamamlandı olarak işaretle
                      </button>
                    ) : (
                      isAdmin && (
                        <button data-testid={`note-reopen-${n.id}`} className="secondary-button" onClick={() => reopen(n)}>
                          <RotateCcw size={14} /> Yeniden aç
                        </button>
                      )
                    )}
                    {isAdmin && (
                      <button data-testid={`note-delete-${n.id}`} className="row-icon tone-danger" onClick={() => remove(n)}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <Pagination page={pg.page} pageCount={pg.pageCount} total={pg.total}
                    onChange={pg.setPage} testid="notes-pagination" />
      </Panel>
    </>
  );
}
